import classNames from 'classnames'
import { TriangleAlert, X } from 'lucide-react'
import { type ReactNode, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Button, Radio, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { CancellationPolicyFields } from '../cancellation-policy-fields'
import { formatPolicyWindow } from '../../utils'
import type {
  AccountPolicyDetailsProps,
  PolicyModeOptionProps,
  TypeCancellationPolicyPanelProps,
} from './type-cancellation-policy-panel.interface'
import classes from './type-cancellation-policy-panel.module.scss'

const OTHER_SETTINGS_PATH = `${routeNames.settings.path}?tab=other-settings`
const CLOSE_LABEL = 'Close and show Booking Form'
const CUSTOM_RESCHEDULE_HELP_ON = 'Clients can move the appointment inside the window.'

function PolicyModeOption({ mode, checked, title, description, onSelect, children }: PolicyModeOptionProps) {
  const id = `type-cancellation-policy-${mode}`
  return (
    <div className={classNames(classes.card, { [classes.cardSelected]: checked })}>
      <div className={classes.cardHeader}>
        <span className={classes.radio}>
          <Radio
            id={id}
            name="type-cancellation-policy-mode"
            value={mode}
            checked={checked}
            aria-labelledby={`${id}-title`}
            aria-describedby={`${id}-description`}
            onChange={() => onSelect(mode)}
          />
        </span>
        <label htmlFor={id} className={classes.cardLabel}>
          <span id={`${id}-title`} className={classes.optionTitle}>
            {title}
          </span>
          <span id={`${id}-description`} className={classes.optionDescription}>
            {description}
          </span>
        </label>
      </div>
      {checked && children && <div className={classes.cardDetails}>{children}</div>}
    </div>
  )
}

/** Opens in a new tab so the unsaved edits in this modal survive (S-625 D24). */
function OtherSettingsLink({ children, className }: { children: ReactNode; className: string }) {
  return (
    <Link to={OTHER_SETTINGS_PATH} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </Link>
  )
}

function AccountPolicyDetails({ account, reschedulePossible }: AccountPolicyDetailsProps) {
  if (!account) return null
  if (!account.enabled) {
    return (
      <div className={classes.accountDetails} data-testid="type-policy-account-off">
        <div className={classes.warning}>
          <TriangleAlert size={20} className={classes.warningIcon} aria-hidden="true" />
          <Typography variant="body" element="span" className={classes.warningText}>
            Your account policy is off, so no policy applies to this type right now.
          </Typography>
        </div>
        <OtherSettingsLink className={classes.buttonLink}>Turn on account policy</OtherSettingsLink>
      </div>
    )
  }
  const rescheduleValue = !reschedulePossible ? 'n/a' : account.allow_reschedule_in_window ? 'Allowed' : 'Blocked'
  const rows = [
    ['Notice window', `${formatPolicyWindow(account.notice_value, account.notice_unit)} before the start`],
    ['Late cancels', account.allow_late_cancel ? 'Allowed, recorded for you' : 'Blocked'],
    ['Reschedules inside', rescheduleValue],
    ['Policy text', account.policy_text],
  ]
  return (
    <div className={classes.accountDetails} data-testid="type-policy-account-summary">
      <dl className={classes.summary}>
        {rows.map(([term, value]) => (
          <div key={term} className={classes.summaryRow}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <OtherSettingsLink className={classes.link}>
        Edit account policy in Other Settings <span aria-hidden="true">&rarr;</span>
      </OtherSettingsLink>
    </div>
  )
}

/** The row that opens the panel sits low in the left column, so the panel top is often scrolled out of view. */
function useRevealOnOpen() {
  const panelRef = useRef<HTMLElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    panelRef.current?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' })
    headingRef.current?.focus({ preventScroll: true })
  }, [])
  return { panelRef, headingRef }
}

export function TypeCancellationPolicyPanel(props: TypeCancellationPolicyPanelProps) {
  const { typeName, mode, onModeChange, account, reschedulePossible, onClose } = props
  const name = typeName.trim() || 'this appointment type'
  const customDescription =
    account?.enabled === false ? `Create a policy just for ${name}.` : `Set a different window or rules for ${name}.`
  const { panelRef, headingRef } = useRevealOnOpen()
  return (
    <section
      ref={panelRef}
      className={classes.panel}
      aria-label="Cancellation Policy"
      data-testid="type-cancellation-policy-panel"
    >
      <div className={classes.header}>
        <Typography ref={headingRef} variant="h5" tabIndex={-1} className={classes.title}>
          Cancellation Policy
        </Typography>
        <button type="button" className={classes.close} aria-label={CLOSE_LABEL} title={CLOSE_LABEL} onClick={onClose}>
          <X size={16} strokeWidth={2.5} aria-hidden="true" />
          Close
        </button>
      </div>
      <fieldset className={classes.fieldset}>
        <legend className={classes.legend}>{`Which policy applies to ${name}`}</legend>
        <PolicyModeOption
          mode="account"
          checked={mode === 'account'}
          title="Use account policy"
          description="Follows the policy in Other Settings."
          onSelect={onModeChange}
        >
          {account && <AccountPolicyDetails account={account} reschedulePossible={reschedulePossible} />}
        </PolicyModeOption>
        <PolicyModeOption
          mode="custom"
          checked={mode === 'custom'}
          title="Custom policy"
          description={customDescription}
          onSelect={onModeChange}
        >
          <div className={classes.customDetails}>
            <Typography variant="body" className={classes.notice}>
              Started as a copy of your account policy.
            </Typography>
            <CancellationPolicyFields
              variant="card"
              control={props.customControl}
              errors={props.customErrors}
              rescheduleNotApplicableNote={props.rescheduleNotApplicableNote}
              rescheduleSettingLabel={props.rescheduleSettingLabel}
              rescheduleHelpOn={CUSTOM_RESCHEDULE_HELP_ON}
              onNoticeChange={props.onNoticeChange}
              onTextChange={props.onTextChange}
            />
          </div>
        </PolicyModeOption>
        <PolicyModeOption
          mode="none"
          checked={mode === 'none'}
          title="No policy"
          description={
            reschedulePossible
              ? 'Customers can cancel and reschedule at any time.'
              : 'Customers can cancel at any time.'
          }
          onSelect={onModeChange}
        />
      </fieldset>
      {mode === 'custom' && (
        <div className={classes.footer}>
          <Button type="button" variant="primary" className={classes.doneButton} onClick={onClose}>
            Done
          </Button>
        </div>
      )}
    </section>
  )
}
